import { Fragment, forwardRef, memo, useImperativeHandle } from 'react';

import { ClickAwayListener } from '@material-ui/core';

import { PopoverStyled } from './Popover.styles';
import { PopoverHandle, PopoverProps } from './Popover.types';
import { usePopoverControls } from './usePopoverControls';

export const Popover = memo(
  forwardRef<PopoverHandle, PopoverProps>(
    (
      {
        toggle,
        open,
        variant = 'click',
        disabled,
        disableClickAway,
        popoverProps,
        toggleProps,
        children,
        onOpen,
        onClose,
      },
      ref,
    ) => {
      const {
        isOpen,
        popoverAnchor,
        popoverActions,
        toggleTriggerProps,
        popoverTriggerProps,
        popoverOpen,
        popoverClose,
        handleToggleClickAway,
      } = usePopoverControls({
        open,
        variant,
        disabled,
        disableClickAway,
        popoverProps,
        toggleProps,
        onOpen,
        onClose,
      });

      useImperativeHandle(ref, () => ({
        open: popoverOpen,
        close: popoverClose,
        isOpen: () => isOpen,
      }));

      return (
        <Fragment>
          <ClickAwayListener onClickAway={handleToggleClickAway}>
            <span
              ref={popoverAnchor}
              aria-owns="mouse-over-popover"
              aria-haspopup="true"
              {...toggleProps}
              {...toggleTriggerProps}
            >
              {toggle}
            </span>
          </ClickAwayListener>

          {!disabled && (
            <PopoverStyled
              id="mouse-over-popover"
              open={isOpen}
              anchorEl={popoverAnchor.current}
              action={popoverActions}
              disableAutoFocus={true}
              disableEnforceFocus={true}
              disableRestoreFocus={true}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              onClose={popoverClose}
              {...popoverProps}
              PaperProps={{ ...popoverProps?.PaperProps, ...popoverTriggerProps }}
              classes={popoverProps?.classes}
            >
              {children}
            </PopoverStyled>
          )}
        </Fragment>
      );
    },
  ),
);
